import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { useNavigate } from "react-router-dom";
import styles from "./threatIncidentManagement.module.scss";
import { threatIncidents } from "./threatIncidentData";

const stats = [
  { label: "Active Incidents", value: 12, tone: "active" },
  { label: "Under Investigation", value: 8, tone: "investigating" },
  { label: "Resolved (7d)", value: 47, tone: "resolved" },
  { label: "Avg MTTR", value: "8.2m", tone: "neutral" },
];

const statusTone = {
  contained: styles.statusContained,
  investigating: styles.statusInvestigating,
  mitigated: styles.statusMitigated,
  monitoring: styles.statusMonitoring,
  resolved: styles.statusResolved,
};

const severityTone = {
  critical: styles.sevCritical,
  high: styles.sevHigh,
  medium: styles.sevMedium,
};

const ThreatIncidentManagement = () => {
  const [range, setRange] = useState("7d");
  const navigate = useNavigate();

  const summaryByTone = useMemo(() => stats, []);

  return (
    <Box className={styles.page}>
      <Box className={styles.pageHeader}>
        <div className={styles.titleBlock}>
          <Typography className={styles.title} fontWeight={600}>
            Threat & Incident Management
          </Typography>
          <Typography className={styles.caption}>
            Active threats and incident response
          </Typography>
        </div>
        <div className={styles.headerActions}>
          <Select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            size="small"
            className={styles.rangeSelect}
          >
            <MenuItem value="24h">24 Hours</MenuItem>
            <MenuItem value="7d">7 Days</MenuItem>
            <MenuItem value="30d">30 Days</MenuItem>
          </Select>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            className={styles.exportButton}
          >
            Export
          </Button>
        </div>
      </Box>

      <Box className={styles.statGrid}>
        {summaryByTone.map((item) => (
          <Card key={item.label} className={styles.statCard}>
            <div className={`${styles.statBadge} ${styles[item.tone] || ""}`}>
              {item.label}
            </div>
            <div className={styles.statValue}>{item.value}</div>
          </Card>
        ))}
      </Box>

      <Card className={styles.listCard}>
        <div className={styles.listHeader}>
          <div>
            <Typography className={styles.listTitle}>
              Active Incidents
            </Typography>
            <Typography className={styles.listCaption}>
              Latest incidents across your environments
            </Typography>
          </div>
          <IconButton
            className={styles.headerIconButton}
            aria-label="More options"
          >
            <MoreHorizOutlinedIcon />
          </IconButton>
        </div>
        <Divider />

        <div className={styles.incidentList}>
          {threatIncidents.map((incident, idx) => (
            <React.Fragment key={incident.id}>
              <div className={styles.incidentRow}>
                <Chip
                  label={incident.severity}
                  size="small"
                  className={`${styles.severity} ${
                    severityTone[incident.severity] || ""
                  }`}
                />
                <Box sx={{ flex: "1" }}>
                  <div className={styles.incidentHeader}>
                    <Typography className={styles.incidentTitle}>
                      {incident.title}
                    </Typography>
                    <span
                      className={`${styles.status} ${
                        statusTone[incident.status] || ""
                      }`}
                    >
                      <AccessTimeRoundedIcon fontSize="inherit" />
                      {incident.status}
                    </span>
                  </div>

                  <Typography className={styles.incidentDesc}>
                    {incident.description}
                  </Typography>

                  <div className={styles.metaRow}>
                    <span className={styles.metaItem}>
                      <strong>Affected:</strong> {incident.affected}
                    </span>
                    <span className={styles.metaItem}>
                      <strong>Assignee:</strong> {incident.assignee}
                    </span>
                    <span className={styles.metaItem}>
                      <strong>Created:</strong> {incident.created}
                    </span>
                    <span className={styles.metaItem}>
                      <strong>Updated:</strong> {incident.updated}
                    </span>
                  </div>

                  <div className={styles.actionsRow}>
                    <Button
                      variant="contained"
                      className={styles.primaryAction}
                      startIcon={<VisibilityOutlinedIcon />}
                      onClick={() =>
                        navigate(`/threats-incidents/${incident.id}`)
                      }
                    >
                      View Details
                    </Button>

                    <Button
                      variant="outlined"
                      className={styles.secondaryAction}
                      startIcon={<CheckRoundedIcon />}
                    >
                      Mark Resolved
                    </Button>
                  </div>
                </Box>
              </div>

              {idx !== threatIncidents.length - 1 ? <Divider /> : null}
            </React.Fragment>
          ))}
        </div>
      </Card>
    </Box>
  );
};

export default ThreatIncidentManagement;
